import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Layout from './Layout/Layout';
import { getCdnImageUrl } from '../utils/cdn';
import './HomePage.css';
import './ItemsStyle.css';

const Region = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const region = location.state?.region || 'NorthEast';
  const [places, setPlaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { 
    fetchPlaces();
  }, [region]);

  const fetchPlaces = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/places/${encodeURIComponent(region.toLowerCase())}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!Array.isArray(data.data)) {
        throw new Error('Data is not an array');
      }
      setPlaces(data.data);
      setError(null);
    } catch (e) {
      console.error('Fetch error:', e);
      setError(`Failed to fetch places for ${region}. Error: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaceSelect = (place) => {
    navigate(`/content-display-one/${encodeURIComponent(region)}/${encodeURIComponent(place.title)}`, { 
      state: { 
        region: region,
        title: place.title,
        id: place._id
      } 
    });
  };

  const PlaceComponent = ({ place }) => {
    const [imgFailed, setImgFailed] = useState(false);
    const imageSrc = place.image ? getCdnImageUrl(place.image) : null;

    return (
      <article className="item" onClick={() => handlePlaceSelect(place)}>
        <h2>{place.title || 'No Title'}</h2>
        <div className="item-content">
          {imageSrc && !imgFailed ? (
            <img
              src={imageSrc}
              alt={place.title || 'No Title'}
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="item-image-placeholder">
              <span>🖼️ Image Coming Soon</span>
            </div>
          )}
          <div
            className="item-description"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(place.description || 'No Description')
            }}
          />
        </div>
      </article>
    );
  };

  return (
    <Layout>
      <Helmet>
        <title>{`${region} Travel Guide | The Half Time Travel`}</title>
        <meta name="description" content={`Explore ${region} with The Half Time Travel. Discover amazing destinations and travel tips.`} />
        <meta property="og:title" content={`${region} Travel Guide | The Half Time Travel`} />
        <meta property="og:description" content={`Plan your ${region} adventure with The Half Time Travel. Explore top destinations and hidden gems!`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelBlog",
            "name": `The Half Time Travel - ${region}`,
            "description": `Explore ${region} with The Half Time Travel`,
            "url": `https://www.thehalftimewhistle.com/${region.toLowerCase()}`
          })}
        </script>
      </Helmet>

      <div className="home-page" style={{ padding: '20px' }}>
        <main>
          <h1>{region} Destinations</h1>
          {isLoading && <div style={{ textAlign: 'center', padding: '40px' }}>Loading destinations...</div>}
          {error && <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</div>}
          
          {!isLoading && !error && (
            <section className="items-container">
              {places.length > 0 ? (
                places.map((place) => (
                  <PlaceComponent
                    key={place._id || place.title}
                    place={place}
                  />
                ))
              ) : (
                <p>No places found for {region}</p>
              )}
            </section>
          )}
        </main>
      </div>
    </Layout>
  );  
};

export default Region;
